import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { toast } from "sonner";
import { userAccountService } from "@/services/userAccount.service";
import { useAuth } from "@/contexts/AuthContext";
import { API_URL } from "@/utils/axios";
import { getAccessToken } from "@/utils/accessTokenStore";
import type {
  ThemeMode,
  ThemePalette,
  ThemePreferences,
} from "@/types/preferences";
import {
  DEFAULT_THEME_PREFERENCES,
  fromApiThemePreferences,
  toApiThemePreferences,
} from "@/types/preferences";
import {
  DEFAULT_DARK_THEME_ID,
  DEFAULT_LIGHT_THEME_ID,
  resolvePaletteForTheme,
} from "@/styles/theme-presets";
import {
  adjustUiScale,
  clampUiScale,
  resolveUiScale,
  toUiScaleFactor,
  UI_SCALE_DEFAULT,
} from "@/utils/uiScale";
import { useLocalization } from "./LocalizationContext";

type ActiveTone = "light" | "dark";

interface ActiveTheme {
  id: string;
  tone: ActiveTone;
  palette: ThemePalette;
}

export interface ThemeContextType {
  themePreferences: ThemePreferences;
  activeTheme: ActiveTheme;
  uiScale: number;
  setUiScale: (value: number) => void;
  increaseUiScale: () => void;
  decreaseUiScale: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  setThemePreset: (tone: ActiveTone, themeId: string) => void;
  setSchedule: (lightTime: string, darkTime: string) => void;
  updateCustomTheme: (
    tone: ActiveTone,
    palette: Partial<ThemePalette>,
    name?: string
  ) => void;
  toggleTheme: () => void;
}

const THEME_STORAGE_KEY = "theme-preferences";
const LEGACY_THEME_STORAGE_KEY = "theme";
const UI_SCALE_STORAGE_KEY = "ui-scale";
const THEME_PERSIST_DEBOUNCE_MS = 600;

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const normalizeStoredPalette = (
  palette: Partial<ThemePalette> | undefined,
  fallback: ThemePalette
): ThemePalette => {
  const legacy = palette as Partial<ThemePalette> & { muted?: string };
  return {
    ...fallback,
    ...(palette ?? {}),
    surface: palette?.surface ?? legacy?.muted ?? fallback.surface,
  };
};

const loadStoredTheme = (): ThemePreferences => {
  if (typeof window === "undefined") return DEFAULT_THEME_PREFERENCES;
  const saved = localStorage.getItem(THEME_STORAGE_KEY);
  if (!saved) {
    const legacy = localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
    if (legacy === "dark" || legacy === "light") {
      return { ...DEFAULT_THEME_PREFERENCES, mode: legacy };
    }
    return DEFAULT_THEME_PREFERENCES;
  }
  try {
    const parsed = JSON.parse(saved) as Partial<ThemePreferences>;
    const custom = parsed.customTheme ?? DEFAULT_THEME_PREFERENCES.customTheme;
    const lightPalette = normalizeStoredPalette(
      custom.light,
      DEFAULT_THEME_PREFERENCES.customTheme.light
    );
    const darkPalette = normalizeStoredPalette(
      custom.dark,
      DEFAULT_THEME_PREFERENCES.customTheme.dark
    );
    return {
      ...DEFAULT_THEME_PREFERENCES,
      ...parsed,
      customTheme: {
        name: custom.name ?? DEFAULT_THEME_PREFERENCES.customTheme.name,
        light: lightPalette,
        dark: darkPalette,
      },
      scheduledLightTime:
        parsed.scheduledLightTime ??
        DEFAULT_THEME_PREFERENCES.scheduledLightTime,
      scheduledDarkTime:
        parsed.scheduledDarkTime ?? DEFAULT_THEME_PREFERENCES.scheduledDarkTime,
    };
  } catch {
    return DEFAULT_THEME_PREFERENCES;
  }
};

const loadStoredUiScale = (): number => {
  if (typeof window === "undefined") return UI_SCALE_DEFAULT;
  const saved = localStorage.getItem(UI_SCALE_STORAGE_KEY);
  const parsed = saved ? parseInt(saved, 10) : NaN;
  return resolveUiScale(Number.isFinite(parsed) ? parsed : UI_SCALE_DEFAULT);
};

const prefersDarkMode = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

const isNightTime = (
  now = new Date(),
  sunTimes?: { sunrise: Date; sunset: Date } | null
) => {
  if (sunTimes) {
    return now < sunTimes.sunrise || now >= sunTimes.sunset;
  }
  const hour = now.getHours();
  return hour >= 20 || hour < 7;
};

const resolveTone = (
  prefs: ThemePreferences,
  systemPrefersDark: boolean,
  sunTimes?: { sunrise: Date; sunset: Date } | null
): ActiveTone => {
  switch (prefs.mode) {
    case "dark":
      return "dark";
    case "light":
      return "light";
    case "sunrise-sunset":
      return isNightTime(new Date(), sunTimes) ? "dark" : "light";
    case "scheduled": {
      const now = new Date();
      const [lightHour, lightMinute] = (prefs.scheduledLightTime || "07:00")
        .split(":")
        .map((value) => parseInt(value, 10));
      const [darkHour, darkMinute] = (prefs.scheduledDarkTime || "20:00")
        .split(":")
        .map((value) => parseInt(value, 10));
      const toMinutes = (h?: number, m?: number) =>
        (Number.isFinite(h ?? NaN) ? h ?? 0 : 0) * 60 +
        (Number.isFinite(m ?? NaN) ? m ?? 0 : 0);
      const lightStart = toMinutes(lightHour, lightMinute);
      const darkStart = toMinutes(darkHour, darkMinute);
      const currentMinutes = now.getHours() * 60 + now.getMinutes();

      if (lightStart === darkStart) {
        return "light";
      }

      if (lightStart < darkStart) {
        // light during [lightStart, darkStart)
        return currentMinutes >= lightStart && currentMinutes < darkStart
          ? "light"
          : "dark";
      }
      // schedule crosses midnight: light from lightStart -> 24h and 0 -> darkStart
      return currentMinutes >= lightStart || currentMinutes < darkStart
        ? "light"
        : "dark";
    }
    case "system":
    default:
      return systemPrefersDark ? "dark" : "light";
  }
};

const resolveActiveTheme = (
  prefs: ThemePreferences,
  systemPrefersDark: boolean,
  sunTimes?: { sunrise: Date; sunset: Date } | null
): ActiveTheme => {
  const tone = resolveTone(prefs, systemPrefersDark, sunTimes);
  const themeId =
    tone === "dark"
      ? prefs.darkTheme || DEFAULT_DARK_THEME_ID
      : prefs.lightTheme || DEFAULT_LIGHT_THEME_ID;
  const palette = resolvePaletteForTheme(themeId, tone, prefs);
  return { id: themeId, tone, palette };
};

const mixHex = (foreground: string, background: string, amount = 0.2) => {
  const toRgbLocal = (hex: string) => {
    const normalized = hex.replace("#", "");
    if (normalized.length !== 6) return { r: 0, g: 0, b: 0 };
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16),
    };
  };
  const fromRgb = ({ r, g, b }: { r: number; g: number; b: number }) =>
    `#${[r, g, b]
      .map((value) =>
        Math.max(0, Math.min(255, Math.round(value)))
          .toString(16)
          .padStart(2, "0")
      )
      .join("")}`;

  const fg = toRgbLocal(foreground);
  const bg = toRgbLocal(background);
  const clamp = (value: number) => Math.max(0, Math.min(1, value));
  const t = clamp(amount);
  return fromRgb({
    r: fg.r * t + bg.r * (1 - t),
    g: fg.g * t + bg.g * (1 - t),
    b: fg.b * t + bg.b * (1 - t),
  });
};

const applyThemeToDocument = (active: ActiveTheme) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (active.tone === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  const fgOnPrimary =
    active.tone === "dark" ? active.palette.foreground : active.palette.background;
  const fgOnAccent =
    active.tone === "dark" ? active.palette.foreground : active.palette.background;
  const surface = active.palette.surface;
  const muted = mixHex(active.palette.foreground, surface, 0.12);
  const mutedForegroundSoft = mixHex(
    active.palette.foreground,
    active.palette.background,
    0.7
  );
  const mutedForeground =
    active.tone === "dark"
      ? mixHex(active.palette.foreground, active.palette.background, 0.8)
      : mutedForegroundSoft;
  const border = mixHex(active.palette.foreground, active.palette.background, 0.12);
  const inputBackground = mixHex(active.palette.foreground, surface, 0.06);
  const switchBackground = mixHex(active.palette.foreground, surface, 0.18);
  const destructive = mixHex(
    active.palette.accent,
    active.palette.primary,
    0.65
  );
  const destructiveForeground =
    active.tone === "dark" ? active.palette.foreground : active.palette.background;
  const primarySoft = mixHex(
    active.palette.primary,
    active.palette.background,
    0.14
  );
  const primarySoftWeak = mixHex(
    active.palette.primary,
    active.palette.background,
    0.1
  );
  const focusRing = mixHex(
    active.palette.primary,
    active.palette.background,
    0.25
  );
  const focusRingSoft = mixHex(
    active.palette.primary,
    active.palette.background,
    0.2
  );

  const setVar = (name: string, value: string) =>
    root.style.setProperty(name, value);
  root.style.colorScheme = active.tone;
  const clearVar = (name: string) => root.style.removeProperty(name);

  setVar("--background", active.palette.background);
  setVar("--surface", surface);
  setVar("--foreground", active.palette.foreground);
  setVar("--card", surface);
  setVar("--card-foreground", active.palette.foreground);
  setVar("--popover", active.palette.background);
  setVar("--popover-foreground", active.palette.foreground);
  setVar("--primary", active.palette.primary);
  setVar("--primary-foreground", fgOnPrimary);
  setVar("--secondary", muted);
  setVar("--secondary-foreground", active.palette.foreground);
  setVar("--muted", muted);
  setVar("--muted-foreground", mutedForeground);
  setVar("--muted-foreground-soft", mutedForegroundSoft);
  setVar("--accent", active.palette.accent);
  setVar("--accent-foreground", fgOnAccent);
  setVar("--destructive", destructive);
  setVar("--destructive-foreground", destructiveForeground);
  setVar("--border", border);
  setVar("--input", surface);
  setVar("--input-background", inputBackground);
  setVar("--switch-background", switchBackground);
  setVar("--ring", active.palette.accent);
  setVar("--primary-soft", primarySoft);
  setVar("--primary-soft-weak", primarySoftWeak);
  setVar("--focus-ring", focusRing);
  setVar("--focus-ring-soft", focusRingSoft);
  if (active.tone === "dark") {
    const slate700 = mixHex(active.palette.primary, surface, 0.18);
    const slate800 = mixHex(active.palette.primary, surface, 0.12);
    const slate900 = mixHex(active.palette.primary, active.palette.background, 0.08);
    const slate950 = mixHex(active.palette.primary, active.palette.background, 0.04);
    setVar("--color-slate-700", slate700);
    setVar("--color-slate-800", slate800);
    setVar("--color-slate-900", slate900);
    setVar("--color-slate-950", slate950);
  } else {
    clearVar("--color-slate-700");
    clearVar("--color-slate-800");
    clearVar("--color-slate-900");
    clearVar("--color-slate-950");
  }
  // Chart palette derived from accent variations
  const accentStrong = active.palette.accent;
  const accentMid = mixHex(
    active.palette.accent,
    active.palette.background,
    0.65
  );
  const accentSoft = mixHex(
    active.palette.accent,
    active.palette.background,
    0.45
  );
  const accentMuted = mixHex(
    active.palette.accent,
    active.palette.background,
    0.25
  );
  const accentBlend = mixHex(
    active.palette.primary,
    active.palette.accent,
    0.5
  );
  setVar("--chart-1", accentStrong);
  setVar("--chart-2", accentMid);
  setVar("--chart-3", accentSoft);
  setVar("--chart-4", accentMuted);
  setVar("--chart-5", accentBlend);
  const categoryScaleSteps =
    active.tone === "dark"
      ? [0.8, 0.65, 0.5, 0.35, 0.2]
      : [0.2, 0.35, 0.5, 0.65, 0.8];
  categoryScaleSteps.forEach((step, index) => {
    setVar(
      `--category-scale-${index + 1}`,
      mixHex(active.palette.primary, active.palette.background, step)
    );
  });
  const mapDensitySteps = (() => {
    const min = 0.2;
    const max = 0.8;
    const steps = 10;
    const delta = (max - min) / (steps - 1);
    return Array.from({ length: steps }, (_, index) => {
      const value = min + delta * index;
      return active.tone === "dark" ? max - (value - min) : value;
    });
  })();
  mapDensitySteps.forEach((step, index) => {
    setVar(
      `--map-density-${index + 1}`,
      mixHex(active.palette.primary, active.palette.background, step)
    );
  });
  setVar("--sidebar", surface);
  setVar("--sidebar-foreground", active.palette.foreground);
  setVar("--sidebar-primary", active.palette.primary);
  setVar("--sidebar-primary-foreground", fgOnPrimary);
  setVar("--sidebar-accent", active.palette.accent);
  setVar("--sidebar-accent-foreground", fgOnAccent);
  setVar("--sidebar-border", border);
  setVar("--sidebar-ring", active.palette.accent);
  const iconSurface = mixHex(
    active.palette.primary,
    active.palette.background,
    0.14
  );
  const iconBorder = mixHex(
    active.palette.primary,
    active.palette.background,
    0.28
  );
  const headingColor =
    active.tone === "dark"
      ? mixHex(active.palette.primary, active.palette.foreground, 0.7)
      : active.palette.primary;
  setVar("--icon-surface", iconSurface);
  setVar("--icon-border", iconBorder);
  setVar("--heading-color", headingColor);
  const mapOutline = mixHex(
    active.palette.primary,
    active.palette.background,
    active.tone === "dark" ? 0.45 : 0.28
  );
  const mapDeselected = mixHex(
    active.palette.primary,
    active.palette.background,
    active.tone === "dark" ? 0.16 : 0.12
  );
  const mapHover = mixHex(active.palette.primary, active.palette.accent, 0.6);
  const mapHoverSelected = mixHex(
    active.palette.primary,
    active.palette.background,
    0.55
  );
  setVar("--map-outline", mapOutline);
  setVar("--map-deselected", mapDeselected);
  setVar("--map-selected", active.palette.primary);
  setVar("--map-all-selected", active.palette.primary);
  setVar("--map-hover", mapHover);
  setVar("--map-hover-selected", mapHoverSelected);
  setVar("--map-label-fill", active.palette.foreground);
  setVar("--map-label-stroke", active.palette.background);
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) {
    themeMeta.setAttribute("content", active.palette.background);
  }
};

const applyUiScaleToDocument = (scale: number) => {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(
    "--ui-scale",
    String(toUiScaleFactor(scale))
  );
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themePreferences, setThemePreferences] = useState<ThemePreferences>(
    () => loadStoredTheme()
  );
  const [uiScale, setUiScaleState] = useState<number>(() => loadStoredUiScale());
  const [systemPrefersDark, setSystemPrefersDark] = useState(prefersDarkMode());
  const [activeTheme, setActiveTheme] = useState<ActiveTheme>(() =>
    resolveActiveTheme(loadStoredTheme(), prefersDarkMode())
  );
  const [sunTimes, setSunTimes] = useState<{
    sunrise: Date;
    sunset: Date;
  } | null>(null);
  const { isAuthenticated } = useAuth();
  const { t } = useLocalization();

  const resolveErrorMessage = useCallback(
    (error: any): string => {
      const message = error?.response?.data?.message;
      if (!message || typeof message !== "string") {
        return t("common.genericError");
      }
      const translated = t(message);
      return translated === message ? message : translated;
    },
    [t]
  );

  const skipRemotePersist = useRef(false);
  const remoteReady = useRef(false);
  const persistTimeoutRef = useRef<number | null>(null);
  const latestPreferencesRef = useRef<ThemePreferences>(themePreferences);
  const latestUiScaleRef = useRef<number>(uiScale);

  useEffect(() => {
    latestPreferencesRef.current = themePreferences;
  }, [themePreferences]);

  useEffect(() => {
    latestUiScaleRef.current = uiScale;
  }, [uiScale]);

  const persistThemePreferences = useCallback(
    async (prefs: ThemePreferences, scale: number) => {
      try {
        await userAccountService.updateThemePreferences({
          themePreferences: toApiThemePreferences(prefs),
          uiScale: clampUiScale(scale),
        });
      } catch (error: any) {
        toast.error(resolveErrorMessage(error));
      }
    },
    [resolveErrorMessage]
  );

  const flushPendingPersist = useCallback(() => {
    if (!isAuthenticated || !remoteReady.current || skipRemotePersist.current) {
      return;
    }
    if (persistTimeoutRef.current === null) {
      return;
    }
    if (typeof window === "undefined") {
      return;
    }
    const prefs = latestPreferencesRef.current;
    const scale = latestUiScaleRef.current;
    const payload = JSON.stringify({
      themePreferences: toApiThemePreferences(prefs),
      uiScale: clampUiScale(scale),
    });
    const token = getAccessToken();
    window.clearTimeout(persistTimeoutRef.current);
    persistTimeoutRef.current = null;
    try {
      void fetch(`${API_URL}/users/me/theme-preferences`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: payload,
        credentials: "include",
        keepalive: true,
      });
    } catch {
      // ignore send errors during page lifecycle changes
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const mql = window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;
    if (!mql) return;
    const handler = (event: MediaQueryListEvent) =>
      setSystemPrefersDark(event.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      remoteReady.current = false;
      setUiScaleState(loadStoredUiScale());
      return;
    }
    let cancelled = false;

    const loadRemotePreferences = async () => {
      try {
        const response = await userAccountService.getThemePreferences();
        if (cancelled) return;
        skipRemotePersist.current = true;
        remoteReady.current = true;
        setThemePreferences(fromApiThemePreferences(response.themePreferences));
        setUiScaleState(resolveUiScale(response.uiScale));
      } catch (error: any) {
        if (cancelled) return;
        remoteReady.current = true;
        toast.error(resolveErrorMessage(error));
      }
    };

    void loadRemotePreferences();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, t]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveTheme(
        resolveActiveTheme(themePreferences, systemPrefersDark, sunTimes)
      );
    }, 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [themePreferences, systemPrefersDark, sunTimes]);

  useEffect(() => {
    const active = resolveActiveTheme(
      themePreferences,
      systemPrefersDark,
      sunTimes
    );
    setActiveTheme(active);
  }, [themePreferences, systemPrefersDark, sunTimes]);

  useEffect(() => {
    applyThemeToDocument(activeTheme);
  }, [activeTheme]);

  useEffect(() => {
    applyUiScaleToDocument(uiScale);
  }, [uiScale]);

  useEffect(() => {
    // Theme preferences are stored locally to match the current settings architecture.
    try {
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(themePreferences));
    } catch {
      // ignore storage errors
    }
  }, [themePreferences]);

  useEffect(() => {
    try {
      localStorage.setItem(UI_SCALE_STORAGE_KEY, String(clampUiScale(uiScale)));
    } catch {
      // ignore storage errors
    }
  }, [uiScale]);

  useEffect(() => {
    if (!isAuthenticated || !remoteReady.current) {
      return;
    }
    if (skipRemotePersist.current) {
      skipRemotePersist.current = false;
      if (persistTimeoutRef.current !== null) {
        window.clearTimeout(persistTimeoutRef.current);
        persistTimeoutRef.current = null;
      }
      return;
    }

    if (persistTimeoutRef.current !== null) {
      window.clearTimeout(persistTimeoutRef.current);
    }

    persistTimeoutRef.current = window.setTimeout(() => {
      persistTimeoutRef.current = null;
      void persistThemePreferences(themePreferences, uiScale);
    }, THEME_PERSIST_DEBOUNCE_MS);

    return () => {
      if (persistTimeoutRef.current !== null) {
        window.clearTimeout(persistTimeoutRef.current);
        persistTimeoutRef.current = null;
      }
    };
  }, [themePreferences, uiScale, isAuthenticated, persistThemePreferences]);

  useEffect(() => {
    const handlePageHide = () => flushPendingPersist();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushPendingPersist();
      }
    };
    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [flushPendingPersist]);

  // Calculate sunrise/sunset for Passau (privacy-friendly, no geolocation)
  useEffect(() => {
    const LAT = 48.5667;
    const LON = 13.4319;
    const rad = Math.PI / 180;
    const J1970 = 2440588;
    const J2000 = 2451545;
    const dayMs = 86400000;
    const degToRad = (deg: number) => deg * rad;
    const toJulian = (date: Date) => date.valueOf() / dayMs - 0.5 + J1970;
    const fromJulian = (j: number) => new Date((j + 0.5 - J1970) * dayMs);
    const toDays = (date: Date) => toJulian(date) - J2000;
    const solarMeanAnomaly = (d: number) => degToRad(357.5291 + 0.98560028 * d);
    const eclipticLongitude = (M: number) => {
      const C =
        degToRad(1.9148) * Math.sin(M) +
        degToRad(0.02) * Math.sin(2 * M) +
        degToRad(0.0003) * Math.sin(3 * M);
      const P = degToRad(102.9372);
      return M + C + P + Math.PI;
    };
    const declination = (L: number, b = 0) =>
      Math.asin(
        Math.sin(b) * Math.cos(degToRad(23.4397)) +
          Math.cos(b) * Math.sin(degToRad(23.4397)) * Math.sin(L)
      );
    const solarTransitJ = (ds: number, M: number, L: number) =>
      J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
    const hourAngle = (h: number, phi: number, d: number) =>
      Math.acos(
        (Math.sin(h) - Math.sin(phi) * Math.sin(d)) /
          (Math.cos(phi) * Math.cos(d))
      );
    const getTimes = (date: Date) => {
      const lw = -degToRad(LON);
      const phi = degToRad(LAT);
      const d = toDays(date);
      const n = Math.round(d - lw / (2 * Math.PI));
      const ds = n + lw / (2 * Math.PI);
      const M = solarMeanAnomaly(ds);
      const L = eclipticLongitude(M);
      const dec = declination(L);
      const h0 = degToRad(-0.833);
      const w = hourAngle(h0, phi, dec);
      const Jtransit = solarTransitJ(ds, M, L);
      const Jrise = Jtransit - w / (2 * Math.PI);
      const Jset = Jtransit + w / (2 * Math.PI);
      return { sunrise: fromJulian(Jrise), sunset: fromJulian(Jset) };
    };

    const refresh = () => {
      const now = new Date();
      const { sunrise, sunset } = getTimes(now);
      setSunTimes({ sunrise, sunset });
      const nextMidnight = new Date(now);
      nextMidnight.setHours(24, 0, 0, 0);
      const timeout = nextMidnight.getTime() - now.getTime() + 1000;
      return window.setTimeout(refresh, timeout);
    };

    const timeoutId = refresh();
    return () => window.clearTimeout(timeoutId);
  }, []);

  const setThemeMode = (mode: ThemeMode) => {
    setThemePreferences((prev) => ({ ...prev, mode }));
  };

  const setThemePreset = (tone: ActiveTone, themeId: string) => {
    setThemePreferences((prev) =>
      tone === "dark"
        ? { ...prev, darkTheme: themeId }
        : { ...prev, lightTheme: themeId }
    );
  };

  const updateCustomTheme = (
    tone: ActiveTone,
    palette: Partial<ThemePalette>,
    name?: string
  ) => {
    setThemePreferences((prev) => {
      const baseCustom =
        prev.customTheme ?? DEFAULT_THEME_PREFERENCES.customTheme;
      const updatedCustom = {
        ...baseCustom,
        name: name ?? baseCustom.name,
        [tone]: { ...baseCustom[tone], ...palette },
      };
      return {
        ...prev,
        customTheme: updatedCustom,
        ...(tone === "dark"
          ? { darkTheme: "custom" }
          : { lightTheme: "custom" }),
      };
    });
  };

  const setSchedule = (lightTime: string, darkTime: string) => {
    setThemePreferences((prev) => ({
      ...prev,
      scheduledLightTime: lightTime || prev.scheduledLightTime,
      scheduledDarkTime: darkTime || prev.scheduledDarkTime,
    }));
  };

  const setUiScale = useCallback((value: number) => {
    setUiScaleState(clampUiScale(value));
  }, []);

  const increaseUiScale = useCallback(() => {
    setUiScaleState((prev) => adjustUiScale(prev, "increase"));
  }, []);

  const decreaseUiScale = useCallback(() => {
    setUiScaleState((prev) => adjustUiScale(prev, "decrease"));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }
      const key = event.key;
      if (key === "+" || key === "=") {
        event.preventDefault();
        increaseUiScale();
        return;
      }
      if (key === "-" || key === "_") {
        event.preventDefault();
        decreaseUiScale();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [decreaseUiScale, increaseUiScale]);

  const toggleTheme = () => {
    const tone = resolveTone(themePreferences, systemPrefersDark, sunTimes);
    const nextMode: ThemeMode = tone === "dark" ? "light" : "dark";
    setThemeMode(nextMode);
  };

  return (
    <ThemeContext.Provider
      value={{
        themePreferences,
        activeTheme,
        uiScale,
        setUiScale,
        increaseUiScale,
        decreaseUiScale,
        setThemeMode,
        setThemePreset,
        setSchedule,
        updateCustomTheme,
        toggleTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
